import { ActorSubclass, HttpAgent } from '@icp-sdk/core/agent'
import { config } from '../appConfig'
import { approveToken } from '../handlers/walletActions/wallet'
import {
  idlFactory as idlWaterNeuronFactory,
  _SERVICE as WaterNeuronFactory,
} from '../idls/waterNeuron/waterNeuronBackend.did'
import { ICToken } from '../interfaces/common'
import { addMultiplier, minBigInt } from '../utils/bigint'
import { createActor } from '../utils/createActor'

const WATER_NEURON_BACKEND_CANISTER_ID = 'tsbvt-pyaaa-aaaar-qafva-cai'

const WTN_AIRDROP_TIERS = [
  { threshold: 80_000_000_000_00n, rate: 8 },
  { threshold: 160_000_000_000_00n, rate: 4 },
  { threshold: 320_000_000_000_00n, rate: 2 },
  { threshold: 640_000_000_000_00n, rate: 1 },
  { threshold: 1_280_000_000_000_00n, rate: 0.5 },
  { threshold: 2_560_000_000_000_00n, rate: 0.25 },
  { threshold: 5_120_000_000_000_00n, rate: 0.125 },
]

export class WaterNeuron {
  private actor: ActorSubclass<WaterNeuronFactory>
  private anonymousActor: ActorSubclass<WaterNeuronFactory>

  constructor(agent: HttpAgent) {
    this.actor = createActor(idlWaterNeuronFactory, {
      canisterId: WATER_NEURON_BACKEND_CANISTER_ID,
      agent,
    })

    this.anonymousActor = createActor(idlWaterNeuronFactory, {
      canisterId: WATER_NEURON_BACKEND_CANISTER_ID,
      agent: config.getAgent(),
    })
  }

  computeRewards(alreadyDistributed: bigint, amount: bigint) {
    let totalRewards = 0n
    let amountToDistribute = amount
    let cumulativeAmount = alreadyDistributed

    WTN_AIRDROP_TIERS.some(({ threshold, rate }) => {
      if (cumulativeAmount > threshold) {
        cumulativeAmount = cumulativeAmount - threshold
      } else {
        const tierAvailable = threshold - cumulativeAmount
        const amountInThisTier = minBigInt(amountToDistribute, tierAvailable)

        cumulativeAmount = 0n
        totalRewards = totalRewards + addMultiplier(amountInThisTier, rate)
        amountToDistribute = amountToDistribute - amountInThisTier

        return amountToDistribute === 0n
      }
    })

    return totalRewards
  }

  async getInfo() {
    return this.anonymousActor.get_info()
  }

  async stake(amount: bigint, icpToken: ICToken) {
    await approveToken({
      standard: icpToken.standard,
      tokenCanisterId: icpToken.address,
      spender: {
        address: WATER_NEURON_BACKEND_CANISTER_ID,
      },
      amount: (amount + icpToken.fee).toString(),
    })

    const result = await this.actor.icp_to_nicp({
      maybe_subaccount: [],
      amount_e8s: BigInt(amount),
    })

    if ('Ok' in result) {
      return result.Ok
    } else {
      throw result.Err
    }
  }

  async unstake(amount: bigint, nicpToken: ICToken) {
    await approveToken({
      standard: nicpToken.standard,
      tokenCanisterId: nicpToken.address,
      spender: {
        address: WATER_NEURON_BACKEND_CANISTER_ID,
      },
      amount: (amount + nicpToken.fee).toString(),
    })

    const result = await this.actor.nicp_to_icp({
      maybe_subaccount: [],
      amount_e8s: amount,
    })

    if ('Ok' in result) {
      return result.Ok
    } else {
      throw result.Err
    }
  }
}
